import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Label, Textarea, Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Sparkles, Fingerprint, ChevronDown, ChevronRight, Layers, Check } from "lucide-react";
import { toast } from "sonner";

export function RecommendationCard({ rec, onAccept, busy, acceptLabel = "Accept & apply template" }) {
  if (!rec) return null;
  const pct = Math.round((rec.confidence || 0) * 100);
  return (
    <div className="card-flat p-5 border-accent/40 space-y-3" data-testid="vp-recommendation-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          <span className="label-eyebrow">AI Recommendation</span>
        </div>
        <Badge variant="lime" data-testid="vp-rec-confidence">{pct}% confidence</Badge>
      </div>
      <div className="font-display text-xl font-bold" data-testid="vp-rec-title">
        {rec.industry_name} → {rec.vertical_name}
        <span className="text-xs font-mono text-muted-foreground ml-2">v{rec.version}</span>
        {rec.is_stub && <Badge variant="outline" className="ml-2 text-[10px]">Starter template</Badge>}
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed" data-testid="vp-rec-explanation">{rec.explanation}</p>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <Badge variant="outline">{rec.activates.stages} value-chain stages</Badge>
        <Badge variant="outline">{rec.activates.attributes} data attributes</Badge>
        <Badge variant="outline">{rec.activates.widgets} dashboard widgets</Badge>
        <Badge variant="outline">{rec.activates.risks} preloaded risks</Badge>
        <Badge variant="outline">{rec.activates.kpis} recommended KPIs</Badge>
        <Badge variant="outline">{rec.activates.supplier_categories} supplier categories</Badge>
        <Badge variant="outline">{rec.activates.financing_opportunities} financing opportunities</Badge>
      </div>
      {onAccept && (
        <Button variant="primary" onClick={() => onAccept(rec)} disabled={busy} data-testid="vp-rec-accept-btn">
          <Check className="h-3.5 w-3.5" /> {busy ? "Applying…" : acceptLabel}
        </Button>
      )}
      {!!rec.alternates?.length && (
        <div className="text-[11px] text-muted-foreground">
          Also considered: {rec.alternates.map((a) => `${a.industry_name} → ${a.vertical_name}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

export function LibraryBrowser({ onApply, busy, applyRole = "primary", appliedCodes = [] }) {
  const [industries, setIndustries] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/valueprint/library").then((r) => setIndustries(r.data.industries)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="vp-library-browser">
      {industries.map((ind) => (
        <div key={ind.key} className="card-flat overflow-hidden">
          <button type="button" onClick={() => setOpen(open === ind.key ? null : ind.key)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-hover transition-colors"
            data-testid={`vp-lib-industry-${ind.key}`}>
            <div className="flex items-center gap-2">
              {open === ind.key ? <ChevronDown className="h-4 w-4 text-accent" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <span className="text-sm font-medium">{ind.name}</span>
              <Badge variant="outline" className="text-[10px]">{ind.verticals.length} verticals</Badge>
            </div>
          </button>
          {open === ind.key && (
            <div className="border-t border-border divide-y divide-border">
              <p className="px-4 py-2 text-[11px] text-muted-foreground">{ind.description}</p>
              {ind.verticals.map((v) => {
                const applied = appliedCodes.includes(v.code);
                return (
                  <div key={v.code} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`vp-lib-vertical-${v.code}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {v.vertical_name}
                        <span className="font-mono text-[10px] text-muted-foreground">v{v.version}</span>
                        {v.is_stub && <Badge variant="outline" className="text-[9px]">Starter</Badge>}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{v.description}</p>
                      <div className="text-[10px] font-mono text-muted-foreground mt-1">
                        {v.counts.stages} stages · {v.counts.attributes} attributes · {v.counts.risks} risks · {v.counts.kpis} KPIs
                      </div>
                    </div>
                    <Button size="sm" variant={applied ? "ghost" : "outline"} disabled={applied || busy}
                      onClick={() => onApply(v.code)} data-testid={`vp-lib-apply-${v.code}`}>
                      {applied ? "Applied" : `Apply as ${applyRole}`}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ApplyWizard({ role = "primary", onApplied, appliedCodes = [], compact = false }) {
  const [desc, setDesc] = useState("");
  const [goals, setGoals] = useState("");
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [browse, setBrowse] = useState(false);

  const recommend = async () => {
    if (desc.trim().length < 10) { toast.error("Describe your business in a sentence or two first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/valueprint/recommend", { business_description: desc, reporting_goals: goals || null });
      setRec(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const apply = async (code, aiConfidence = null) => {
    setApplying(true);
    try {
      const { data } = await api.post("/valueprint/apply", { code, role, ai_confidence: aiConfidence });
      toast.success(`Template applied — ${data.provisioned.risks} risks, ${data.provisioned.supplier_categories} supplier categories and ${data.provisioned.widgets} widgets activated`);
      onApplied && onApplied();
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(false); }
  };

  return (
    <div className="space-y-4" data-testid="vp-apply-wizard">
      {!compact && (
        <div className="card-flat p-6 text-center space-y-2">
          <Fingerprint className="h-10 w-10 text-accent mx-auto" />
          <h2 className="font-display text-2xl font-bold">Select your industry &amp; value-chain template</h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Your Lestari ValuePrint tailors the whole platform to how your business actually creates value —
            dashboard widgets, risks, supplier categories, data attributes and KPIs, all activated from one template.
          </p>
        </div>
      )}

      <div className="card-flat p-5 space-y-3">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /><span className="label-eyebrow">Describe your business — AI recommends a template</span></div>
        <div>
          <Label>What does your organisation do?</Label>
          <Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)}
            placeholder="e.g. We design, build and maintain landscapes for commercial and government clients across Singapore…"
            data-testid="vp-wizard-description" />
        </div>
        <div>
          <Label>Reporting goals (optional)</Label>
          <Input value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="e.g. voluntary ESG report for clients, SGX-aligned" data-testid="vp-wizard-goals" />
        </div>
        <div className="flex gap-2">
          <Button variant="primary" onClick={recommend} disabled={busy} data-testid="vp-wizard-recommend-btn">
            <Sparkles className="h-3.5 w-3.5" /> {busy ? "Analysing…" : "Get AI recommendation"}
          </Button>
          <Button variant="ghost" onClick={() => setBrowse(!browse)} data-testid="vp-wizard-browse-btn">
            <Layers className="h-3.5 w-3.5" /> {browse ? "Hide library" : "Browse the template library"}
          </Button>
        </div>
      </div>

      <RecommendationCard rec={rec} busy={applying} onAccept={(r) => apply(r.code, r.confidence)} />

      {browse && <LibraryBrowser onApply={(code) => apply(code)} busy={applying} applyRole={role} appliedCodes={appliedCodes} />}
    </div>
  );
}
